import React, { useState } from 'react';
import {
  useCreateContractTemplateMutation,
  useUpdateContractTemplateMutation,
} from '../Service/contractTemplatesApi';
import { useUploadContractTemplateLogoMutation } from '../Service/extendedContractTemplatesApi';
import { ContractTemplatesRoutes } from '../../../Router/path';
import DescriptionIcon from '@mui/icons-material/Description';
import InfoIcon from '@mui/icons-material/Info';
import SettingsIcon from '@mui/icons-material/Settings';
import PhotoIcon from '@mui/icons-material/Photo';
import EntityFormLayout from '../../Shared/Components/EntityFormLayout/EntityFormLayout';
import { FormRenderGroupTypes } from '../../Shared/Components/FormRenderer/FormRenderGroup.types';
import { useEntityForm } from '../../Shared/Hooks/useEntityForm';
import { contractTemplateFields } from '../fields';
import { contractTemplateInitialValues } from '../initialValues';
import AccordionCard from '../../Shared/Components/AccordionCard/AccordionCard';
import UploadImagesForm from '../../Shared/Components/UploadImagesForm/UploadImagesForm';
import ContractTemplateLogoGallery from '../Components/ContractTemplateLogoGallery';
import { Typography } from '@mui/material';

const CreateContractTemplate = () => {
  const [createContractTemplate, createStatus] = useCreateContractTemplateMutation();
  const [updateContractTemplate, updateStatus] = useUpdateContractTemplateMutation();
  const [uploadLogo, uploadLogoStatus] = useUploadContractTemplateLogoMutation();
  const [createdContractTemplateId, setCreatedContractTemplateId] = useState<number | null>(null);
  const [logoUrl, setLogoUrl] = useState<string>('');

  const mutationStatus = createdContractTemplateId ? updateStatus : createStatus;

  const entityForm = useEntityForm({
    initialData: contractTemplateInitialValues,
    fields: contractTemplateFields,
    submitFn: (data) => {
      if (createdContractTemplateId) {
        return updateContractTemplate({ id: createdContractTemplateId, data }).unwrap();
      } else {
        return createContractTemplate(data).unwrap();
      }
    },
    redirectUrl: ContractTemplatesRoutes.root,
    title: 'Dodaj szablon kontraktu',
    icon: <DescriptionIcon />,
    backUrl: ContractTemplatesRoutes.root,
    groupOrder: [FormRenderGroupTypes.basic, FormRenderGroupTypes.configuration],
    groupIcons: {
      Podstawowe: <InfoIcon sx={{ color: (theme) => theme.palette.primary.main }} />,
      Konfiguracja: <SettingsIcon sx={{ color: (theme) => theme.palette.primary.main }} />,
    },
    showSuccessBeforeRedirect: true,
    redirectDelay: 2000,
    onSuccessWithResponse: (response) => {
      if (typeof response === 'number') {
        setCreatedContractTemplateId(response);
      }
    },
    mutationStatus,
  });

  return (
    <>
      <EntityFormLayout {...entityForm}>
        <AccordionCard
          title="Logo szablonu kontraktu"
          icon={<PhotoIcon fontSize="large" color="primary" />}
        >
          {createdContractTemplateId ? (
            <>
              <UploadImagesForm
                onUploadSuccess={() => {
                  // Po udanym uploadzie odśwież stronę lub pobierz dane ponownie
                  console.log('Logo uploaded successfully');
                }}
                useUploadMutation={() => [
                  async (params: { contractTemplateId: number; files: File[] }) => {
                    const result = await uploadLogo(params).unwrap();
                    // Zaktualizuj logoUrl z wyniku uploadu
                    if (result?.logoUrl) {
                      setLogoUrl(result.logoUrl);
                    }
                    return result;
                  },
                  uploadLogoStatus,
                ]}
                mutationParams={(files: File[]) => ({
                  contractTemplateId: createdContractTemplateId,
                  files,
                })}
              />

              {logoUrl && (
                <ContractTemplateLogoGallery
                  logoUrl={logoUrl}
                  onDelete={() => setLogoUrl('')}
                  showDeleteButton={true}
                />
              )}
            </>
          ) : (
            <Typography color="text.secondary">
              Logo możesz dodać dopiero po zapisaniu szablonu kontraktu.
            </Typography>
          )}
        </AccordionCard>
      </EntityFormLayout>
    </>
  );
};

export default CreateContractTemplate;
